import { IUpdateProfileType } from '@introspection/index';
import { Inject, Injectable } from '@rxdi/core';
import { LocalStorageService, SocialService } from '@stradivario/graphql-server';
import { BehaviorSubject, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { BaseGraphqlService } from '../base';

interface UserOptions {
  marmaid?: boolean;
}

@Injectable()
export class UserService extends BaseGraphqlService {
  @Inject(SocialService)
  private socialService: SocialService;
  @Inject(LocalStorageService)
  private localStorage: LocalStorageService;

  private cache = true;
  private customToken: string;
  user_id: string;

  private userOptions: BehaviorSubject<UserOptions>;

  OnInit() {
    this.userOptions = new BehaviorSubject<UserOptions>(this.localStorage.get('user-settings'));
    this.getUserOptions()
      .pipe(
        filter((options) => !!options?.marmaid),
        switchMap(() => import('../../../shared/mermaid/mermaid.component'))
      )
      .subscribe();
  }

  getUserOptions() {
    return this.userOptions.asObservable();
  }

  setUserOptions(options: UserOptions) {
    this.userOptions.next(options);
    this.localStorage.update('user-settings', options);
  }
  canViewProfileSession() {
    return this.getUserOnce().pipe(
      take(1),
      map((u) => !!u.scopes?.includes('CAN_VIEW_PROFILE_SESSIONS'))
    );
  }

  setCache(cache: boolean) {
    this.cache = cache;
  }
  getUser() {
    return this.socialService.getUser().pipe(
      map((res) => res),
      tap((res) => (this.user_id = res.uid)),
      switchMap((res) => this.getUserQuery(res.uid))
    );
  }

  updateProfile(payload: IUpdateProfileType) {
    return this.mutate({ mutation: 'update-profile.mutation.graphql', variables: { payload } });
  }

  generateCustomToken() {
    if (this.customToken) {
      return of(this.customToken);
    }
    return this.mutate({ mutation: 'generate-custom-token.graphql' }).pipe(
      map(({ data }) => data.generateCustomToken.token),
      tap((token) => (this.customToken = token))
    );
  }

  getUserById(id: string) {
    return this.query({
      query: 'get-user-by-id.query.graphql',
      variables: {
        id,
      },
    }).pipe(map(({ data }) => data.getUserById));
  }

  getUserOnce() {
    return this.socialService.getUser().pipe(
      map((res) => res.uid),
      tap((id) => (this.user_id = id)),
      switchMap(() =>
        this.query({
          query: 'get-user.query.graphql',
          variables: { user_id: this.user_id },
        }).pipe(map(({ data }) => data.me))
      )
    );
  }

  getUserQuery(user_id: string) {
    return this.watchQuery({
      query: 'get-user.query.graphql',
      errorPolicy: 'all',
      fetchPolicy: this.cache ? null : 'network-only',
      variables: { user_id },
    }).pipe(map(({ data }) => data.me));
  }

  listUsers() {
    return this.query({
      query: 'list-users.graphql',
    }).pipe(map(({ data }) => data.listUsers));
  }
}
